import React, { useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { Loader2, AlertCircle, Eye, EyeOff, MailCheck } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import PublicHeader from '../components/common/PublicHeader';

const Register = () => {
  const [searchParams] = useSearchParams();
  const { register } = useAuth();

  const planTier = searchParams.get('plan');
  const billingInterval = searchParams.get('interval') || 'monthly';

  const [form, setForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    firm_name: '',
    password: '',
    confirm_password: '',
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [registeredEmail, setRegisteredEmail] = useState('');

  const passwordValid = form.password.length >= 8
    && /[A-Za-z]/.test(form.password)
    && /\d/.test(form.password);

  const passwordsMatch = form.password === form.confirm_password;

  const canSubmit = form.first_name && form.last_name && form.email
    && form.firm_name && passwordValid && passwordsMatch && !loading;

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmit) return;

    setError('');
    setLoading(true);

    const result = await register({
      first_name: form.first_name,
      last_name: form.last_name,
      email: form.email,
      firm_name: form.firm_name,
      password: form.password,
    });

    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    // Registration issues no session now — the address has to be verified
    // first. Remember the plan they picked so the first login after verifying
    // can take them to plan selection instead of the dashboard.
    if (planTier) {
      localStorage.setItem('pendingPlan', JSON.stringify({ tier: planTier, interval: billingInterval }));
    }

    setLoading(false);
    setRegisteredEmail(result.email || form.email);
  };

  if (registeredEmail) {
    return (
      <div className="min-h-screen bg-gray-50">
        <PublicHeader />

        <div className="max-w-md mx-auto px-4 py-12">
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8 text-center">
            <MailCheck className="w-12 h-12 text-yellow-500 mx-auto" />
            <h1 className="text-2xl font-bold text-gray-900 mt-4">Check your email</h1>
            <p className="text-sm text-gray-600 mt-2">
              We sent a verification link to <span className="font-medium text-gray-900">{registeredEmail}</span>.
              Click it to confirm your address, then log in to finish setting up
              {planTier ? ' your plan.' : ' your firm.'}
            </p>
            <p className="text-xs text-gray-400 mt-4">
              The link expires in 48 hours. Check your spam folder if it hasn't arrived.
            </p>

            <Link
              to="/login"
              className="mt-6 inline-flex w-full justify-center py-2.5 px-4 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 transition-all"
            >
              Go to log in
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <PublicHeader />

      <div className="max-w-md mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-8">
          <div className="text-center mb-6">
            <h1 className="text-2xl font-bold text-gray-900">Create your account</h1>
            <p className="text-sm text-gray-500 mt-1">
              {planTier
                ? 'Set up your firm and start your 30-day free trial'
                : 'Get started with JustPractice'}
            </p>
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-sm text-red-700">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">First name</label>
                <input
                  type="text"
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Last name</label>
                <input
                  type="text"
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Firm name</label>
              <input
                type="text"
                name="firm_name"
                value={form.firm_name}
                onChange={handleChange}
                required
                placeholder="e.g. Smith & Associates"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
              <input
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {form.password && !passwordValid && (
                <p className="text-xs text-red-500 mt-1">
                  Must be at least 8 characters with at least one letter and one number
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Confirm password</label>
              <input
                type={showPassword ? 'text' : 'password'}
                name="confirm_password"
                value={form.confirm_password}
                onChange={handleChange}
                required
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500 outline-none"
              />
              {form.confirm_password && !passwordsMatch && (
                <p className="text-xs text-red-500 mt-1">Passwords do not match</p>
              )}
            </div>

            <button
              type="submit"
              disabled={!canSubmit}
              className="w-full py-2.5 px-4 text-sm font-medium text-white bg-yellow-500 rounded-lg hover:bg-yellow-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Creating account...
                </>
              ) : (
                'Create Account'
              )}
            </button>
          </form>

          <p className="text-center text-sm text-gray-500 mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-yellow-600 hover:text-yellow-700 font-medium">
              Log in
            </Link>
          </p>

          <p className="text-center text-xs text-gray-400 mt-4">
            By creating an account, you agree to our{' '}
            <Link to="/terms-of-service" className="underline hover:text-gray-600">Terms of Service</Link>
            {' '}and{' '}
            <Link to="/privacy-policy" className="underline hover:text-gray-600">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Register;
